package com.arena_web.data.websocket

import com.arena_web.core.config.AppConfig
import com.arena_web.core.logger.getLogger
import com.arena_web.data.dto.*
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

sealed class ChatWsEvent {
    data class NewMessage(val message: MessageDto) : ChatWsEvent()
    data class MessageDeleted(val data: MessageDeletedData) : ChatWsEvent()
    data class MessageEdited(val data: MessageEditedData) : ChatWsEvent()
    data class UserTyping(val data: UserTypingData) : ChatWsEvent()
    data class UserOnline(val data: UserOnlineStatusData) : ChatWsEvent()
    data class UserOffline(val data: UserOnlineStatusData) : ChatWsEvent()
    data class ReactionAdded(val data: ReactionChangedData) : ChatWsEvent()
    data class ReactionRemoved(val data: ReactionChangedData) : ChatWsEvent()
    data class MemberJoined(val data: ChatMemberDto) : ChatWsEvent()
    data class MemberLeft(val userId: String) : ChatWsEvent()
    data class MemberKicked(val data: ChatMemberKickedData) : ChatWsEvent()
    data object Connected : ChatWsEvent()
    data object Disconnected : ChatWsEvent()
}

class ChatWebSocketClient {
    private val logger = getLogger<ChatWebSocketClient>()
    private val json = Json { ignoreUnknownKeys = true; isLenient = true }
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)

    private var currentChatId: String? = null
    private var socket: ManagedWebSocket? = null

    private val _events = MutableSharedFlow<ChatWsEvent>(extraBufferCapacity = 64)
    val events: SharedFlow<ChatWsEvent> = _events.asSharedFlow()

    fun connect(chatId: String) {
        disconnect()
        currentChatId = chatId
        socket = ManagedWebSocket(
            name = "Chat($chatId)",
            scope = scope,
            buildUrl = { token -> "${AppConfig.wsUrl}/chats/$chatId?token=$token" },
            onText = ::handleMessage,
            onConnected = { _events.tryEmit(ChatWsEvent.Connected) },
            onDisconnected = { _events.tryEmit(ChatWsEvent.Disconnected) },
        ).also { it.start() }
    }

    fun disconnect() {
        socket?.stop()
        socket = null
        currentChatId = null
    }

    fun sendTyping() {
        try {
            socket?.send("""{"type":"TYPING"}""")
        } catch (e: Exception) {
            logger.error("Failed to send typing: ${e.message}")
        }
    }

    private fun handleMessage(text: String) {
        try {
            val jsonElement = json.parseToJsonElement(text)
            val obj = jsonElement.jsonObject
            val type = obj["type"]?.jsonPrimitive?.content ?: return
            val data = obj["data"] ?: return

            val event = when (type) {
                "CHAT_MESSAGE" -> ChatWsEvent.NewMessage(
                    json.decodeFromJsonElement(MessageDto.serializer(), data)
                )
                "MESSAGE_DELETED" -> ChatWsEvent.MessageDeleted(
                    json.decodeFromJsonElement(MessageDeletedData.serializer(), data)
                )
                "MESSAGE_EDITED" -> ChatWsEvent.MessageEdited(
                    json.decodeFromJsonElement(MessageEditedData.serializer(), data)
                )
                "USER_TYPING" -> ChatWsEvent.UserTyping(
                    json.decodeFromJsonElement(UserTypingData.serializer(), data)
                )
                "USER_ONLINE" -> ChatWsEvent.UserOnline(
                    json.decodeFromJsonElement(UserOnlineStatusData.serializer(), data)
                )
                "USER_OFFLINE" -> ChatWsEvent.UserOffline(
                    json.decodeFromJsonElement(UserOnlineStatusData.serializer(), data)
                )
                "MESSAGE_REACTION_ADDED" -> ChatWsEvent.ReactionAdded(
                    json.decodeFromJsonElement(ReactionChangedData.serializer(), data)
                )
                "MESSAGE_REACTION_REMOVED" -> ChatWsEvent.ReactionRemoved(
                    json.decodeFromJsonElement(ReactionChangedData.serializer(), data)
                )
                "CHAT_MEMBER_JOINED" -> ChatWsEvent.MemberJoined(
                    json.decodeFromJsonElement(ChatMemberDto.serializer(), data)
                )
                "CHAT_MEMBER_LEFT" -> {
                    val userId = extractUserId(data)
                    if (userId != null) ChatWsEvent.MemberLeft(userId) else null
                }
                "CHAT_MEMBER_KICKED" -> ChatWsEvent.MemberKicked(
                    json.decodeFromJsonElement(ChatMemberKickedData.serializer(), data)
                )
                else -> {
                    logger.debug("Unknown WS event type: $type")
                    null
                }
            }

            if (event != null) {
                _events.tryEmit(event)
            }
        } catch (e: Exception) {
            logger.error("Failed to parse WS message: ${e.message}")
        }
    }

    private fun extractUserId(data: JsonElement): String? {
        return try {
            data.jsonObject["userId"]?.jsonPrimitive?.content
        } catch (_: Exception) {
            null
        }
    }

}
